import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import LookupSelect from '../components/LookupSelect';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';
import { lookupFacility, lookupProvider } from '../lookups';

/**
 * Provider admin — which venues a user is allowed to manage.
 *
 * This page hands out access, so every change is stated before it happens. The
 * old page removes a provider or a facility on a bare `delete` button with no
 * confirmation at all, and removes the admin behind a `confirm()` that names the
 * user but not the consequence.
 *
 * Adding is one lookup and one button, rather than the old page's modal that
 * collects a basket of providers and posts them together. Same endpoint — it
 * takes a list — but a basket you can half-fill is a basket you can forget to
 * submit, and this page is not where anyone wants to discover that.
 */

const ProviderAdminInternal = ({ api }) => {
  const { providerAdminId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [newProvider, setNewProvider] = useState('');
  const [newFacility, setNewFacility] = useState('');

  const load = useCallback(async () => {
    try {
      const answer = await api.providerAdmin.getProviderAdminDetailsById(providerAdminId);
      if (!answer?.success || !answer.data) {
        setFailed(true);
        return;
      }
      setData(answer.data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, providerAdminId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/provider-admin">← All provider admins</BackToList>
        <div className="next-note next-note--error">
          Could not load this provider admin.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="next-page next-muted">Loading…</div>;

  const providers = data.providers || [];
  const facilities = data.facilities || [];

  const run = async (fn, okText, failText) => {
    setBusy(true);
    try {
      const result = await fn();
      if (result?.success === false) {
        setNote({ tone: 'error', text: failText });
        throw new Error(failText);
      }
      setNote({ tone: 'info', text: okText });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const addProvider = async () => {
    if (!newProvider) return;
    // The endpoint takes a list; one at a time is a list of one.
    await run(
      () => api.providerAdmin.addProviders([{ value: newProvider }], providerAdminId),
      'Provider added.',
      'Unable to add the provider.'
    ).catch(() => {});
    setNewProvider('');
  };

  const addFacility = async () => {
    if (!newFacility) return;
    await run(
      () => api.providerAdmin.addFacilities([{ value: newFacility }], providerAdminId),
      'Facility added.',
      'Unable to add the facility.'
    ).catch(() => {});
    setNewFacility('');
  };

  const removeProvider = provider => ask({
    kind: 'danger',
    title: `Remove ${provider.providerName || 'this provider'}?`,
    context: `Remove provider · ${data.userName || 'provider admin'}`,
    body: `${data.userName || 'This user'} stops being able to manage ${provider.providerName || 'this provider'} and everything under it.`,
    confirmLabel: 'Remove provider',
    onConfirm: () => run(
      () => api.providerAdmin.removeProvider(provider.providerId, providerAdminId),
      'Provider removed.',
      'Unable to remove the provider.'
    )
  });

  const removeFacility = facility => ask({
    kind: 'danger',
    title: `Remove ${facility.facilityName || 'this facility'}?`,
    context: `Remove facility · ${data.userName || 'provider admin'}`,
    body: `${data.userName || 'This user'} stops being able to manage ${facility.facilityName || 'this facility'}.`,
    confirmLabel: 'Remove facility',
    onConfirm: () => run(
      () => api.providerAdmin.removeFacility(facility.facilityId, providerAdminId),
      'Facility removed.',
      'Unable to remove the facility.'
    )
  });

  const removeAdmin = () => ask({
    kind: 'danger',
    title: `Remove ${data.userName || 'this user'} as a provider admin?`,
    context: `Remove admin · ${data.userEmail || providerAdminId}`,
    body: `All ${providers.length} provider${providers.length === 1 ? '' : 's'} and ${facilities.length} facilit${facilities.length === 1 ? 'y' : 'ies'} below stop being manageable by them. Their account itself is untouched.`,
    warning: 'Granting it again means adding every provider and facility back by hand.',
    confirmLabel: 'Remove as admin',
    onConfirm: async () => {
      const result = await api.providerAdmin.removeUserFromProviderAdmin(providerAdminId);
      if (result?.success === false) {
        setNote({ tone: 'error', text: 'Unable to remove them as an admin.' });
        throw new Error('failed');
      }
      navigate('/next/provider-admin');
    }
  });

  const table = (rows, columns, empty) => (
    <div className="next-table-wrap">
      <table className="next-table next-table--wide next-table--stack">
        <thead>
          <tr>
            {columns.map(([, label]) => <th key={label}>{label}</th>)}
            <th className="next-table__right">Access</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="next-muted next-table__note">{empty}</td>
            </tr>
          )}
          {rows.map(row => (
            <tr key={columns[0][2](row)}>
              {columns.map(([kind, label, get, to], index) => (
                <td key={label} data-label={index === 0 ? '' : label}>
                  {kind === 'id'
                    ? <Guid value={get(row)} to={to(row)} />
                    : (get(row) || <span className="next-muted">—</span>)}
                </td>
              ))}
              <td className="next-table__right" data-label="">
                <button
                  type="button"
                  className="next-btn next-btn--sm next-btn--danger"
                  disabled={busy}
                  onClick={() => (columns === PROVIDER_COLUMNS ? removeProvider(row) : removeFacility(row))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/provider-admin">← All provider admins</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{data.userName || 'Provider admin'}</h1>
          <span className="next-pill next-pill--neutral">
            {providers.length} provider{providers.length === 1 ? '' : 's'} · {facilities.length} facilit{facilities.length === 1 ? 'y' : 'ies'}
          </span>
        </div>
        <span className="next-detail__id">{data.providerAdminId || providerAdminId}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>User</h2></div>
            <div className="next-facts next-facts--single">
              <div className="next-fact">
                <span className="next-fact__label">Name</span>
                <span className="next-fact__value">{data.userName || '—'}</span>
              </div>
              <div className="next-fact">
                <span className="next-fact__label">Email</span>
                <span className="next-fact__value">{data.userEmail || '—'}</span>
              </div>
              <div className="next-fact">
                <span className="next-fact__label">User id</span>
                <span className="next-fact__value">
                  {data.userId
                    ? <Guid value={data.userId} to={`/next/user/${data.userId}`} />
                    : <span className="next-muted">—</span>}
                </span>
              </div>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Providers</h2>
              <span className="next-muted">every facility under these is managed too</span>
            </div>

            <div className="next-filters__row">
              <LookupSelect
                label="Add a provider"
                value={newProvider}
                onChange={setNewProvider}
                loadOptions={key => lookupProvider(api, key)}
                placeholder="Name or id"
              />
              <div className="next-filters__buttons">
                <button
                  type="button"
                  className="next-btn next-btn--primary"
                  disabled={!newProvider || busy}
                  onClick={addProvider}
                >
                  Add provider
                </button>
              </div>
            </div>

            {table(providers, PROVIDER_COLUMNS, 'No provider — this admin manages none.')}
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Facilities</h2>
              <span className="next-muted">single venues, on top of the providers above</span>
            </div>

            <div className="next-filters__row">
              <LookupSelect
                label="Add a facility"
                value={newFacility}
                onChange={setNewFacility}
                loadOptions={key => lookupFacility(api, key)}
                placeholder="Name or id"
              />
              <div className="next-filters__buttons">
                <button
                  type="button"
                  className="next-btn next-btn--primary"
                  disabled={!newFacility || busy}
                  onClick={addFacility}
                >
                  Add facility
                </button>
              </div>
            </div>

            {table(facilities, FACILITY_COLUMNS, 'No facility on its own.')}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button
                type="button"
                className="next-actionlist__item next-actionlist__item--danger"
                onClick={removeAdmin}
              >
                Remove as admin
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                data.userId && { label: 'User', hint: data.userEmail, to: `/next/user/${data.userId}` },
                { label: 'All provider admins', to: '/next/provider-admin' }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

// [kind, label, value, route]
const PROVIDER_COLUMNS = [
  ['id', 'Provider id', row => row.providerId, row => `/next/provider/${row.providerId}`],
  ['text', 'Provider', row => row.providerName]
];

const FACILITY_COLUMNS = [
  ['id', 'Facility id', row => row.facilityId, row => `/next/facility/${row.facilityId}`],
  ['text', 'Facility', row => row.facilityName]
];

ProviderAdminInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const ProviderAdmin = withApi(ProviderAdminInternal);

ProviderAdmin.requiresLogin = true;

export default ProviderAdmin;
